<template>
  <tr>
    <td class="tw:align-middle">
      <div class="tw:flex tw:items-center tw:gap-3">
        <UserAvatar :user="permission.user" :size="36" />
        <div class="tw:min-w-0 tw:space-y-0.5">
          <div v-if="hasName">
            <strong class="tw:font-semibold">
              {{ permission.user.firstname }}
              <span class="text-uppercase">{{ permission.user.lastname }}</span>
            </strong>
          </div>

          <div>
            <span class="tw:text-gray-500">
              {{ permission.user.email }}
            </span>
          </div>

          <template v-if="permission.user.company">
            <div>{{ permission.user.company }}</div>
          </template>

          <template v-if="isPendingInvitation">
            <!--
              A badge reads as a label, not as something to open. This is a link, underlined at
              rest because the project's .btn-link only underlines on hover, and it keeps the
              colour the state deserves: the link blue while the invitation stands, red once it
              has expired.
            -->
            <button
              type="button"
              data-testid="invitation"
              class="tw:inline-flex tw:items-center tw:self-start tw:p-0 tw:border-0 tw:bg-transparent tw:text-sm tw:underline tw:underline-offset-2 tw:cursor-pointer tw:whitespace-nowrap"
              :class="isInvitationExpired
                ? 'tw:text-red-600 tw:hover:text-red-700'
                : 'tw:text-primary tw:hover:opacity-80'"
              @click="showInvitationDetails = true"
            >
              {{ isInvitationExpired ? $t('Invitation expired') : $t('Pending invitation') }}
            </button>

            <InvitationDetails
              :show="showInvitationDetails"
              :user="permission.user"
              :is-resending="isResending"
              :read-only="readOnly"
              @closed="showInvitationDetails = false"
              @resend="resendInvitation"
            />
          </template>
        </div>
      </div>
    </td>
    <td class="tw:align-middle">
        <span v-if="permission.is_owner" class="badge badge-warning">{{
            $t("Admin")
          }}</span>
        <span v-else class="badge badge-light">
          {{$t("Member")}}
        </span>
    </td>
    <td class="tw:align-middle">
      <template v-if="!permission.is_owner">
        <div class="tw:flex tw:gap-1 tw:flex-wrap">
          <span
            v-for="(ability) in permissionAbilities.slice(0, 2)"
            :key="ability.value"
            class="badge badge-light tw:cursor-default tw:whitespace-nowrap"
          >
            {{ $t(ability.label) }}
          </span>
          <TooltipProvider v-if="permissionAbilities.length > 2" :disabled="!permissionAbilities.length">
            <Tooltip>
              <TooltipTrigger asChild>
                <span class="badge badge-light tw:cursor-default tw:whitespace-nowrap">
                  +{{ $t('numberOfPermissions', permissionAbilities.length - 2) }}
                </span>
              </TooltipTrigger>
              <TooltipContent class="tw:text-wrap tw:max-w-64" side="top">
                <ul class="tw:list-disc tw:pl-2">
                  <li v-for="ability in permissionAbilities" :key="ability.value">
                    {{ $t(ability.label) }}
                  </li>
                </ul>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </template>
    </td>
    <td v-if="!readOnly" class="tw:align-middle">
      <button
        class="btn btn-sm btn-outline-primary"
        type="button"
        @click="$emit('edit')"
      >
        {{ $t("Edit") }}
      </button>
    </td>
  </tr>
</template>

<script setup>
import { computed, ref } from "vue";
import axios from "axios";
import { toast } from "vue-sonner";
import { useI18n } from "vue-i18n";
import { UserStatusEnum } from "@/types/enums";
import InvitationDetails from "@/components/permission/InvitationDetails.vue";
import UserAvatar from "@/components/user/UserAvatar.vue";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";

const props = defineProps({
  permissionResourceUrl: {
    type: String,
    default: null,
  },
  userInvitationUrl: {
    type: String,
    default: null,
  },
  permissibleType: {
    type: String,
    default: null,
  },
  permissibleId: {
    type: Number,
    default: null,
  },
  permission: {
    default: {},
  },
  abilities: {
    default: [],
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["edit", "invitationResent"]);

const { t } = useI18n();
const isResending = ref(false);
const showInvitationDetails = ref(false);

// An invited account holds no name until its owner gives one, so the email carries the row.
const hasName = computed(
  () => !!(props.permission.user?.firstname || props.permission.user?.lastname)
);

const permissionAbilities = computed(() => {
  if (!props.permission.abilities || !props.permission.abilities.length) {
    return [];
  }
  return props.abilities.filter((a) => props.permission.abilities.includes(a.value));
})

const isPendingInvitation = computed(
  () => props.permission.user?.status === UserStatusEnum.PENDING_INVITATION
);

const isInvitationExpired = computed(
  () => props.permission.user?.pending_invitation?.is_expired === true
);

const resendInvitation = async () => {
  isResending.value = true;

  try {
    const { data } = await axios.post(`${props.userInvitationUrl}/${props.permission.user.id}/resend`, {
      permissible_type: props.permissibleType,
      permissible_id: props.permissibleId,
    });

    // The row still reports the link it replaced, expiry date included, until the refreshed
    // account comes back.
    emit('invitationResent', data.user);

    showInvitationDetails.value = false;

    toast.success(t('The invitation has been sent again.'));
  } catch (error) {
    toast.error(
      error.response?.data?.message?.user?.[0]
      ?? t('The invitation could not be sent again.')
    );
  } finally {
    isResending.value = false;
  }
};
</script>
